import { useCallback, useState } from 'react'
import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import { EditorView } from 'prosemirror-view'
import { Schema } from 'prosemirror-model'
import { setBlockType } from 'prosemirror-commands'
import { TrailingIcon } from '@/assets/icons'
import { AnalyticsEvent } from '@/analytics/schema/events.schema'
import { TrackEventFn } from '@/analytics/hooks/useAnalytics'

export enum TextSizeLabel {
  NORMAL_TEXT = 'Normal Text',
  HEADING_1 = 'Heading 1',
  HEADING_2 = 'Heading 2',
  HEADING_3 = 'Heading 3',
}

const DEFAULT_TEXT_STYLE = TextSizeLabel.NORMAL_TEXT

interface TextSizeOption {
  label: string
  nodeType: string
  level?: number
  className: string
}

interface TextSizeDropdownProps {
  editorView: EditorView | null
  schema: Schema
  currentTextStyle?: string
  artifactId?: string
  artifactVersionId?: string
  conversationId?: string
  sessionId?: string
  matterId?: string
  trackEvent?: TrackEventFn
}

export default function TextSizeDropdown({
  editorView,
  schema,
  currentTextStyle,
  artifactId,
  artifactVersionId,
  conversationId,
  sessionId,
  matterId,
  trackEvent,
}: TextSizeDropdownProps) {
  const [isOpen, setIsOpen] = useState(false)

  const textSizeOptions: TextSizeOption[] = [
    {
      label: TextSizeLabel.NORMAL_TEXT,
      nodeType: 'paragraph',
      className: 'text-size-normal',
    },
    {
      label: TextSizeLabel.HEADING_1,
      nodeType: 'heading',
      level: 1,
      className: 'text-size-h1',
    },
    {
      label: TextSizeLabel.HEADING_2,
      nodeType: 'heading',
      level: 2,
      className: 'text-size-h2',
    },
    {
      label: TextSizeLabel.HEADING_3,
      nodeType: 'heading',
      level: 3,
      className: 'text-size-h3',
    },
  ]

  const getCurrentTextSize = useCallback(() => {
    // Use prop value if provided (real-time updates), otherwise fallback to callback
    if (currentTextStyle) {
      return currentTextStyle
    }

    if (!editorView) return DEFAULT_TEXT_STYLE

    const { $from } = editorView.state.selection
    const node = $from.parent

    if (node.type.name === 'heading') {
      const level = node.attrs.level
      return textSizeOptions.find((opt) => opt.nodeType === 'heading' && opt.level === level)?.label || DEFAULT_TEXT_STYLE
    } else if (node.type.name === 'paragraph') {
      return DEFAULT_TEXT_STYLE
    }

    return DEFAULT_TEXT_STYLE
  }, [editorView, currentTextStyle])

  const handleTextSizeChange = useCallback(
    (option: TextSizeOption) => {
      if (!editorView || !schema) return

      const nodeType = schema.nodes[option.nodeType]
      if (!nodeType) return

      const attrs = option.level ? { level: option.level } : undefined
      const command = setBlockType(nodeType, attrs)

      const fromSize = getCurrentTextSize() as TextSizeLabel

      command(editorView.state, editorView.dispatch)
      editorView.focus()

      // Track text size change for analytics
      if (artifactId && trackEvent && artifactVersionId && conversationId && sessionId) {
        trackEvent(AnalyticsEvent.ArtifactEditorTextSizeChanged, {
          fromSize,
          toSize: option.label as TextSizeLabel,
          artifactId,
          artifactVersionId,
          conversationId,
          sessionId,
          matterId: matterId ?? 'none',
        })
      }
    },
    [editorView, schema, artifactId, trackEvent, getCurrentTextSize]
  )

  if (!editorView) return null

  return (
    <DropdownMenu.Root onOpenChange={setIsOpen}>
      <DropdownMenu.Trigger asChild>
        <button
          className={`text-size-dropdown-trigger ${isOpen ? 'text-size-dropdown-trigger-open' : ''}`}
          aria-label={`Text size selector. Current size: ${getCurrentTextSize()}`}
          aria-expanded={isOpen}
          aria-haspopup="true"
        >
          <span className="text-size-dropdown-label">{getCurrentTextSize()}</span>
          <div className="text-size-dropdown-arrow-container" aria-hidden="true">
            <TrailingIcon className="text-size-dropdown-arrow" />
          </div>
        </button>
      </DropdownMenu.Trigger>

      <DropdownMenu.Portal>
        <DropdownMenu.Content className="text-size-dropdown-content" sideOffset={4} align="start">
          {textSizeOptions.map((option) => (
            <DropdownMenu.Item
              key={`${option.nodeType}-${option.level || 'default'}`}
              className="text-size-dropdown-item"
              onSelect={() => handleTextSizeChange(option)}
            >
              <span className={option.className}>{option.label}</span>
            </DropdownMenu.Item>
          ))}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  )
}
